import { Injectable } from '@angular/core';

export type UserTier = 'normal' | 'aliado' | 'partner';
export type OrderStatus = 'pendiente_pago' | 'pendiente' | 'confirmado' | 'enviado' | 'entregado' | 'cancelado';
export type DevolucionStatus = 'solicitada' | 'aprobada' | 'mercancia_recibida' | 'reembolsada' | 'rechazada';
export type CouponType = 'porcentaje' | 'fijo';
export type TransferStatus = 'pendiente' | 'en_transito' | 'completado';

export type AdminRol = 'superadmin' | 'gerente' | 'vendedor' | 'almacen';
export type SeccionAdmin =
  'dashboard' | 'clientes' | 'pedidos' | 'productos' | 'categorias' | 'marcas' |
  'inventarios' | 'cupones' | 'reportes' |
  'administradores' | 'envios' | 'configuracion' | 'puntos_venta';

export const SECCIONES_ADMIN: { key: SeccionAdmin; label: string }[] = [
  { key: 'dashboard',       label: 'Dashboard' },
  { key: 'clientes',        label: 'Clientes' },
  { key: 'pedidos',         label: 'Pedidos' },
  { key: 'productos',       label: 'Productos' },
  { key: 'categorias',      label: 'Categorías' },
  { key: 'marcas',          label: 'Marcas' },
  { key: 'inventarios',     label: 'Inventarios' },
  { key: 'puntos_venta',    label: 'Puntos de venta' },
  { key: 'cupones',         label: 'Cupones' },
  { key: 'reportes',        label: 'Reportes' },
  { key: 'administradores', label: 'Administradores' },
  { key: 'envios',          label: 'Envíos' },
  { key: 'configuracion',   label: 'Configuración' },
];

export const PERMISOS_BASE: Record<AdminRol, SeccionAdmin[]> = {
  superadmin: ['dashboard','clientes','pedidos','productos','categorias','marcas','inventarios','puntos_venta','cupones','reportes','administradores','envios','configuracion'],
  gerente:    ['dashboard','clientes','pedidos','productos','categorias','marcas','inventarios','puntos_venta','cupones','reportes','envios','configuracion'],
  vendedor:   ['dashboard','clientes','pedidos','productos'],
  almacen:    ['dashboard','inventarios','productos'],
};

export interface Administrador {
  id: string;
  nombre: string;
  apellido: string;
  telefono: string;
  pin: string;
  rol: AdminRol;
  permisosExtra: Partial<Record<SeccionAdmin, boolean>>;
  activo: boolean;
  fechaCreacion: string;
  bodegaId?: string;
}

export interface Vendedor {
  id: string;
  nombre: string;
  telefono: string;
}

export interface Turno {
  id: string;
  bodegaId: string;
  bodegaNombre: string;
  adminId: string;
  adminNombre: string;
  efectivoInicial: number;
  efectivoEsperado?: number;
  efectivoFinal?: number;
  diferencia?: number;
  status: 'abierto' | 'cerrado';
  abiertoEn: string;
  cerradoEn?: string;
  notas?: string;
  numVentas: number;
  totalVendido: number;
}

export type PuntoMovimientoTipo = 'compra' | 'ajuste' | 'canje' | 'bono';

export interface PuntoMovimiento {
  id: string;
  fecha: string;
  tipo: PuntoMovimientoTipo;
  cantidad: number;   // positivo = ganados, negativo = usados/quitados
  saldo: number;      // saldo resultante después del movimiento
  descripcion: string;
}

export interface Cliente {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  tipo: UserTier;
  puntos: number;
  fechaRegistro: string;
  totalCompras: number;
  pedidos: number;
  vendedorId?: string;
  clave?: string;
  historialPuntos: PuntoMovimiento[];
  direccion?: string;
  telefonoVerificado?: boolean;
  telefonoVerificadoEn?: string;
}

export interface DevolucionItem {
  productoId?: string;
  nombre: string;
  qty: number;
  precio: number;
  color?: string;
}

export interface Devolucion {
  id: string;
  pedidoId: string;
  clienteId?: string;
  clienteNombre: string;
  fecha: string;
  status: DevolucionStatus;
  items: DevolucionItem[];
  motivo: string;
  notas?: string;
  reembolsoTotal: number;
  origen?: 'online' | 'pos';
  turnoId?: string;
  atendidoPorAdminId?: string;
  metodoReembolso?: 'efectivo' | 'puntos' | 'pendiente';
}

export interface Pedido {
  id: string;
  clienteNombre: string;
  clienteId?: string;
  clienteTelefono?: string;
  clienteEmail?: string;
  fecha: string;
  subtotal: number;
  pointsUsed: number;
  total: number;
  status: OrderStatus;
  metodoPago: 'efectivo' | 'transferencia' | 'tarjeta' | 'mixto';
  // Desglose real cuando metodoPago es 'mixto' (o siempre, para pedidos ya
  // migrados) — cada método aporta su propio monto real a la venta.
  pagos?: { metodo: 'efectivo' | 'transferencia' | 'tarjeta'; monto: number }[];
  items: { productoId?: string; nombre: string; qty: number; precio: number; color?: string; de_proveedor?: boolean }[];
  direccionEnvio?: string;
  paqueteria?: string;
  numeroGuia?: string;
  notas?: string;
  devolucionId?: string;
  cuponCodigo?: string;
  descuentoCupon?: number;
  puntosGanados?: number;
  stripePaymentIntentId?: string;
  metodoEnvio?: string;
  costoEnvio?: number;
  origen?: 'online' | 'pos';
  sucursalId?: string;
  turnoId?: string;
  atendidoPorAdminId?: string;
}

// ── Categorías y atributos ────────────────────────────────────────────────────
export type AtributoTipo = 'texto' | 'select' | 'numero' | 'booleano' | 'fecha';

export interface AtributoDefinicion {
  key: string;
  label: string;
  tipo: AtributoTipo;
  opciones?: string[];
  requerido: boolean;
  esVariante: boolean;
}

export interface CategoriaProducto {
  id: string;
  nombre: string;
  emoji: string;
  parentId?: string | null;
  atributos: AtributoDefinicion[];
}

export interface VarianteProducto {
  id: string;
  atributos: Record<string, string>;
  precioOverride?: number;
  activo: boolean;
}
// ─────────────────────────────────────────────────────────────────────────────

export interface Producto {
  id: number;
  nombre: string;
  marca: string;
  categoria: string;
  categoriaId?: string;
  descripcion?: string;
  precio: number;
  precioAliado: number;
  precioPartner: number;
  costo?: number;
  stock: number;
  emoji: string;
  badge?: string;
  activo: boolean;
  colores?: string[];
  atributosVariante?: { dim: string; valores: string[] }[];
  manejaPorColor?: boolean;
  imagenes?: ProductoImagen[];
  atributosProducto?: Record<string, any>;
  variantes?: VarianteProducto[];
  descuentoNormal?:  number;
  descuentoAliado?:  number;
  descuentoPartner?: number;
}

export interface Sucursal {
  id: string;
  nombre: string;
  tipo: 'fijo' | 'movil';
  direccion: string;
}

export interface InventarioItem {
  productoId: number;
  productoNombre: string;
  sucursalId: string;
  color?: string;
  stock: number;
}

export interface Transferencia {
  id: string;
  origen: string;
  destino: string;
  productoNombre: string;
  cantidad: number;
  fecha: string;
  status: TransferStatus;
}

export type MovimientoTipo = 'entrada' | 'transferencia' | 'ajuste';

export interface MovimientoInventario {
  id: string;
  loteId: string;
  tipo: MovimientoTipo;
  productoId: number;
  productoNombre: string;
  cantidad: number;
  color?: string;
  sucursalOrigenId?: string;
  sucursalDestinoId?: string;
  nota: string;
  fecha: string;
}

export interface Cupon {
  id: string;
  codigo: string;
  tipo: CouponType;
  valor: number;
  fechaInicio: string;
  fechaFin: string;
  usoMaximo: number;
  usosActuales: number;
  activo: boolean;
  tiposCliente?: UserTier[]; // undefined / [] = todos los tiers
}

export interface VentaDia {
  fecha: string;
  ventas: number;
  pedidos: number;
}

export interface MargenesConfig {
  normal:  number;
  aliado:  number;
  partner: number;
}

export interface PuntosConfig {
  normal:  number;  // pts ganados por cada $10 de compra
  aliado:  number;
  partner: number;
}

export interface AccionAdmin {
  id: string;
  fecha: string;
  hora: string;
  adminId: string;
  adminNombre: string;
  seccion: string;
  accion: string;
  detalle: string;
}

export interface ColorCatalogo {
  nombre: string;
  hex:    string;
}

export interface ProductoImagen {
  url:         string;
  colorNombre?: string;
}

@Injectable({ providedIn: 'root' })
export class MockDataService {
  margenesConfig: MargenesConfig = {
    normal:  2.50,
    aliado:  1.35,
    partner: 1.25,
  };

  puntosConfig: PuntosConfig = {
    normal:  1,
    aliado:  2,
    partner: 3,
  };

  calcularPuntos(total: number, tier: UserTier): number {
    return Math.floor((total / 10) * this.puntosConfig[tier]);
  }

  auditLog: AccionAdmin[] = [];

  registrarAccion(adminId: string, adminNombre: string, seccion: string, accion: string, detalle: string) {
    const now = new Date();
    this.auditLog.unshift({
      id: `LOG-${Date.now()}`,
      fecha: now.toISOString().slice(0, 10),
      hora:  now.toTimeString().slice(0, 5),
      adminId, adminNombre, seccion, accion, detalle,
    });
    if (this.auditLog.length > 200) this.auditLog.length = 200;
  }

  colorCatalogo: ColorCatalogo[] = [
    { nombre: 'Negro',        hex: '#111111' },
    { nombre: 'Blanco',       hex: '#f0f0f0' },
    { nombre: 'Gris',         hex: '#9ca3af' },
    { nombre: 'Gris oscuro',  hex: '#4b5563' },
    { nombre: 'Plata',        hex: '#c0c0c0' },
    { nombre: 'Rojo',         hex: '#ef4444' },
    { nombre: 'Rojo oscuro',  hex: '#991b1b' },
    { nombre: 'Rosa',         hex: '#f472b6' },
    { nombre: 'Rosa pastel',  hex: '#fbcfe8' },
    { nombre: 'Naranja',      hex: '#fb923c' },
    { nombre: 'Amarillo',     hex: '#fbbf24' },
    { nombre: 'Dorado',       hex: '#d97706' },
    { nombre: 'Verde',        hex: '#22c55e' },
    { nombre: 'Verde militar',hex: '#4b5320' },
    { nombre: 'Verde menta',  hex: '#6ee7b7' },
    { nombre: 'Azul',         hex: '#3b82f6' },
    { nombre: 'Azul marino',  hex: '#1e3a5f' },
    { nombre: 'Azul cielo',   hex: '#38bdf8' },
    { nombre: 'Morado',       hex: '#a855f7' },
    { nombre: 'Lila',         hex: '#c4b5fd' },
    { nombre: 'Beige',        hex: '#d2b48c' },
    { nombre: 'Café',         hex: '#92400e' },
    { nombre: 'Vino',         hex: '#7f1d1d' },
    { nombre: 'Turquesa',     hex: '#2dd4bf' },
  ];

  hexForColor(nombre: string): string {
    return this.colorCatalogo.find(c => c.nombre === nombre)?.hex ?? '#888888';
  }

  // Genera todas las combinaciones de variantes (producto cartesiano de atributosVariante)
  // Retrocompatible: si no hay atributosVariante, usa colores[]
  variantesProducto(p: Producto): string[] {
    if (p.atributosVariante?.length) {
      return p.atributosVariante.reduce<string[]>((combos, dim) => {
        if (!combos.length) return dim.valores;
        return combos.flatMap(c => dim.valores.map(v => `${c} / ${v}`));
      }, []);
    }
    return p.colores ?? [];
  }

  // Extrae el hex de color de una clave de variante compuesta ("Negro / M" → #111111, "Jalapeño" → null)
  hexForVariante(variante: string): string | null {
    if (!variante) return null;
    for (const part of variante.split(' / ').map(s => s.trim())) {
      const found = this.colorCatalogo.find(c => c.nombre === part);
      if (found) return found.hex;
    }
    return null;
  }

  imagenParaColor(producto: Producto, colorNombre: string): string | undefined {
    const imgs = producto.imagenes ?? [];
    return (imgs.find(i => i.colorNombre === colorNombre) ?? imgs[0])?.url;
  }

  imgUrl(producto: Producto): string {
    return producto.imagenes?.[0]?.url ?? '';
  }

  inicial(nombre: string): string {
    return (nombre?.trim()?.[0] ?? '?').toUpperCase();
  }

  readonly vendedores: Vendedor[] = [
    { id: 'VEN-001', nombre: 'Luis Hernández',  telefono: '5599001122' },
    { id: 'VEN-002', nombre: 'Carmen Vidal',    telefono: '5533445566' },
    { id: 'VEN-003', nombre: 'Jorge Reyes',     telefono: '5511998877' },
    { id: 'VEN-004', nombre: 'Diana Castro',    telefono: '5577001234' },
  ];

  readonly clientes: Cliente[] = [
    {
      id: 'CP-10000001', nombre: 'Ana García López', email: 'ana@gmail.com', telefono: '5512345678',
      tipo: 'partner', puntos: 1250, fechaRegistro: '2026-01-15', totalCompras: 12500, pedidos: 18,
      vendedorId: 'VEN-001', clave: 'AGL-2025-01',
      direccion: 'Av. Insurgentes Sur 1234, Col. Del Valle, CDMX, CP 03100',
      historialPuntos: [
        { id: 'PM-001', fecha: '2026-01-18', tipo: 'compra',  cantidad:  200, saldo:  200, descripcion: 'Pedido #CP-90001' },
        { id: 'PM-002', fecha: '2026-02-05', tipo: 'bono',    cantidad:  500, saldo:  700, descripcion: 'Bono bienvenida Partner' },
        { id: 'PM-003', fecha: '2026-03-10', tipo: 'compra',  cantidad:  150, saldo:  850, descripcion: 'Pedido #CP-90015' },
        { id: 'PM-004', fecha: '2026-04-02', tipo: 'canje',   cantidad: -100, saldo:  750, descripcion: 'Canje en tienda' },
        { id: 'PM-005', fecha: '2026-05-01', tipo: 'compra',  cantidad:  300, saldo: 1050, descripcion: 'Pedido #CP-90028' },
        { id: 'PM-006', fecha: '2026-05-14', tipo: 'compra',  cantidad:  200, saldo: 1250, descripcion: 'Pedido #CP-924501' },
      ],
    },
    {
      id: 'CP-10000002', nombre: 'Carlos Martínez', email: 'carlos@gmail.com', telefono: '5587654321',
      tipo: 'aliado', puntos: 430, fechaRegistro: '2026-03-02', totalCompras: 4300, pedidos: 7,
      direccion: 'Calle Morelos 45, Col. Centro, Guadalajara, Jal., CP 44100',
      historialPuntos: [
        { id: 'PM-011', fecha: '2026-03-05', tipo: 'compra',  cantidad:  200, saldo: 200, descripcion: 'Pedido #CP-90044' },
        { id: 'PM-012', fecha: '2026-04-15', tipo: 'compra',  cantidad:  150, saldo: 350, descripcion: 'Pedido #CP-90067' },
        { id: 'PM-013', fecha: '2026-05-02', tipo: 'bono',    cantidad:  130, saldo: 480, descripcion: 'Bonificación mes de abril' },
        { id: 'PM-014', fecha: '2026-05-13', tipo: 'canje',   cantidad:  -50, saldo: 430, descripcion: 'Canje en pedido #CP-924490' },
      ],
    },
    {
      id: 'CP-10000003', nombre: 'María Rodríguez', email: 'maria@yahoo.com', telefono: '5598765432',
      tipo: 'normal', puntos: 85, fechaRegistro: '2026-06-10', totalCompras: 850, pedidos: 2,
      direccion: 'Blvd. Adolfo López Mateos 678, Col. Los Pinos, León, Gto., CP 37200',
      historialPuntos: [
        { id: 'PM-021', fecha: '2026-06-11', tipo: 'compra', cantidad: 85, saldo: 85, descripcion: 'Pedido #CP-924460' },
      ],
    },
    {
      id: 'CP-10000004', nombre: 'José Hernández', email: 'jose@gmail.com', telefono: '5511223344',
      tipo: 'aliado', puntos: 720, fechaRegistro: '2026-02-20', totalCompras: 7200, pedidos: 11,
      vendedorId: 'VEN-001', clave: 'JHZ-2025-03',
      direccion: 'Av. Constitución 890, Col. San Pedro, Monterrey, N.L., CP 64700',
      historialPuntos: [
        { id: 'PM-031', fecha: '2026-02-25', tipo: 'compra',  cantidad:  300, saldo: 300, descripcion: 'Pedido #CP-90088' },
        { id: 'PM-032', fecha: '2026-03-18', tipo: 'compra',  cantidad:  200, saldo: 500, descripcion: 'Pedido #CP-90099' },
        { id: 'PM-033', fecha: '2026-04-10', tipo: 'ajuste',  cantidad:  -80, saldo: 420, descripcion: 'Corrección por devolución' },
        { id: 'PM-034', fecha: '2026-05-12', tipo: 'compra',  cantidad:  300, saldo: 720, descripcion: 'Pedido #CP-924478' },
      ],
    },
    {
      id: 'CP-10000005', nombre: 'Laura Sánchez', email: 'laura@hotmail.com', telefono: '5544332211',
      tipo: 'normal', puntos: 120, fechaRegistro: '2026-08-01', totalCompras: 1200, pedidos: 3,
      historialPuntos: [
        { id: 'PM-041', fecha: '2026-08-03', tipo: 'compra', cantidad: 120, saldo: 120, descripcion: 'Pedido #CP-90111' },
      ],
    },
    {
      id: 'CP-10000006', nombre: 'Roberto Jiménez', email: 'rjimenez@gmail.com', telefono: '5566778899',
      tipo: 'partner', puntos: 3400, fechaRegistro: '2024-11-05', totalCompras: 34000, pedidos: 45,
      vendedorId: 'VEN-002', clave: 'RJM-2024-07',
      direccion: 'Av. Juárez 567, Col. Centro Histórico, CDMX, CP 06600',
      historialPuntos: [
        { id: 'PM-051', fecha: '2025-12-01', tipo: 'compra',  cantidad:  500, saldo:  500, descripcion: 'Pedido #CP-80011' },
        { id: 'PM-052', fecha: '2026-01-10', tipo: 'compra',  cantidad:  800, saldo: 1300, descripcion: 'Pedido #CP-80034' },
        { id: 'PM-053', fecha: '2026-02-05', tipo: 'bono',    cantidad: 1000, saldo: 2300, descripcion: 'Bono por volumen Q4' },
        { id: 'PM-054', fecha: '2026-03-01', tipo: 'compra',  cantidad:  600, saldo: 2900, descripcion: 'Pedido #CP-80055' },
        { id: 'PM-055', fecha: '2026-04-15', tipo: 'ajuste',  cantidad:  200, saldo: 3100, descripcion: 'Corrección sistema anterior' },
        { id: 'PM-056', fecha: '2026-05-14', tipo: 'compra',  cantidad:  300, saldo: 3400, descripcion: 'Pedido #CP-924498' },
      ],
    },
    {
      id: 'CP-10000007', nombre: 'Patricia Flores', email: 'paty@gmail.com', telefono: '5533221100',
      tipo: 'normal', puntos: 60, fechaRegistro: '2026-09-12', totalCompras: 600, pedidos: 1,
      historialPuntos: [
        { id: 'PM-061', fecha: '2026-09-14', tipo: 'compra', cantidad: 60, saldo: 60, descripcion: 'Pedido #CP-90125' },
      ],
    },
    {
      id: 'CP-10000008', nombre: 'Miguel Torres', email: 'miguel@gmail.com', telefono: '5577889900',
      tipo: 'aliado', puntos: 890, fechaRegistro: '2026-04-18', totalCompras: 8900, pedidos: 13,
      vendedorId: 'VEN-003', clave: 'MTR-2025-02',
      direccion: 'Av. Revolución 234, Col. Narvarte Poniente, CDMX, CP 03600',
      historialPuntos: [
        { id: 'PM-071', fecha: '2026-04-20', tipo: 'compra', cantidad: 350, saldo: 350, descripcion: 'Pedido #CP-90077' },
        { id: 'PM-072', fecha: '2026-05-01', tipo: 'compra', cantidad: 300, saldo: 650, descripcion: 'Pedido #CP-90088' },
        { id: 'PM-073', fecha: '2026-05-08', tipo: 'bono',   cantidad: 240, saldo: 890, descripcion: 'Bono cliente activo' },
      ],
    },
    { id: 'CP-10000009', nombre: 'Isabel Vargas',      email: 'isabel.v@gmail.com',   telefono: '5521304050', tipo: 'normal',  puntos: 45,   fechaRegistro: '2026-09-20', totalCompras: 450,   pedidos: 1,  historialPuntos: [{ id: 'PM-081', fecha: '2026-09-21', tipo: 'compra', cantidad: 45,  saldo: 45,  descripcion: 'Pedido #CP-90130' }] },
    { id: 'CP-10000010', nombre: 'Fernando López',     email: 'flopez@hotmail.com',   telefono: '5534561234', tipo: 'aliado',  puntos: 560,  fechaRegistro: '2026-03-15', totalCompras: 5600,  pedidos: 9,  vendedorId: 'VEN-002', clave: 'FLO-2025-04', historialPuntos: [{ id: 'PM-091', fecha: '2026-03-16', tipo: 'compra', cantidad: 300, saldo: 300, descripcion: 'Pedido #CP-90140' }, { id: 'PM-092', fecha: '2026-04-28', tipo: 'compra', cantidad: 260, saldo: 560, descripcion: 'Pedido #CP-90155' }] },
    { id: 'CP-10000011', nombre: 'Sofía Pérez',        email: 'sofiaperez@yahoo.com', telefono: '5567891011', tipo: 'normal',  puntos: 200,  fechaRegistro: '2026-05-03', totalCompras: 2000,  pedidos: 4,  historialPuntos: [{ id: 'PM-101', fecha: '2026-05-04', tipo: 'compra', cantidad: 200, saldo: 200, descripcion: 'Pedido #CP-90160' }] },
    { id: 'CP-10000012', nombre: 'Alejandro Castro',   email: 'acastro@gmail.com',    telefono: '5512131415', tipo: 'partner', puntos: 2100, fechaRegistro: '2025-08-10', totalCompras: 21000, pedidos: 30, vendedorId: 'VEN-001', clave: 'ACS-2024-09', historialPuntos: [{ id: 'PM-111', fecha: '2025-09-01', tipo: 'compra', cantidad: 800, saldo: 800, descripcion: 'Pedido #CP-80090' }, { id: 'PM-112', fecha: '2026-01-15', tipo: 'bono', cantidad: 1000, saldo: 1800, descripcion: 'Bono Partner Q3' }, { id: 'PM-113', fecha: '2026-04-10', tipo: 'compra', cantidad: 300, saldo: 2100, descripcion: 'Pedido #CP-90170' }] },
    { id: 'CP-10000013', nombre: 'Valeria Moreno',     email: 'vale.moreno@gmail.com',telefono: '5516171819', tipo: 'normal',  puntos: 30,   fechaRegistro: '2026-10-01', totalCompras: 300,   pedidos: 1,  historialPuntos: [{ id: 'PM-121', fecha: '2026-10-02', tipo: 'compra', cantidad: 30,  saldo: 30,  descripcion: 'Pedido #CP-90180' }] },
    { id: 'CP-10000014', nombre: 'Eduardo Ramírez',    email: 'eramirez@outlook.com', telefono: '5520212223', tipo: 'aliado',  puntos: 340,  fechaRegistro: '2026-02-28', totalCompras: 3400,  pedidos: 6,  vendedorId: 'VEN-004', historialPuntos: [{ id: 'PM-131', fecha: '2026-03-01', tipo: 'compra', cantidad: 200, saldo: 200, descripcion: 'Pedido #CP-90190' }, { id: 'PM-132', fecha: '2026-04-20', tipo: 'compra', cantidad: 140, saldo: 340, descripcion: 'Pedido #CP-90200' }] },
    { id: 'CP-10000015', nombre: 'Natalia Guzmán',     email: 'natalia.g@gmail.com',  telefono: '5524252627', tipo: 'normal',  puntos: 95,   fechaRegistro: '2026-07-14', totalCompras: 950,   pedidos: 2,  historialPuntos: [{ id: 'PM-141', fecha: '2026-07-15', tipo: 'compra', cantidad: 95,  saldo: 95,  descripcion: 'Pedido #CP-90210' }] },
    { id: 'CP-10000016', nombre: 'Diego Mendoza',      email: 'dmendoza@gmail.com',   telefono: '5528293031', tipo: 'aliado',  puntos: 780,  fechaRegistro: '2026-01-22', totalCompras: 7800,  pedidos: 12, vendedorId: 'VEN-003', clave: 'DMZ-2025-01', historialPuntos: [{ id: 'PM-151', fecha: '2026-01-25', tipo: 'compra', cantidad: 400, saldo: 400, descripcion: 'Pedido #CP-90220' }, { id: 'PM-152', fecha: '2026-03-12', tipo: 'bono', cantidad: 200, saldo: 600, descripcion: 'Bono referido' }, { id: 'PM-153', fecha: '2026-05-05', tipo: 'compra', cantidad: 180, saldo: 780, descripcion: 'Pedido #CP-90235' }] },
    { id: 'CP-10000017', nombre: 'Camila Herrera',     email: 'camila.h@gmail.com',   telefono: '5532333435', tipo: 'normal',  puntos: 150,  fechaRegistro: '2026-06-28', totalCompras: 1500,  pedidos: 3,  historialPuntos: [{ id: 'PM-161', fecha: '2026-06-29', tipo: 'compra', cantidad: 150, saldo: 150, descripcion: 'Pedido #CP-90240' }] },
    { id: 'CP-10000018', nombre: 'Andrés Navarro',     email: 'anavarro@gmail.com',   telefono: '5536373839', tipo: 'partner', puntos: 1800, fechaRegistro: '2025-10-18', totalCompras: 18000, pedidos: 25, vendedorId: 'VEN-002', clave: 'ANV-2024-10', historialPuntos: [{ id: 'PM-171', fecha: '2025-11-01', tipo: 'compra', cantidad: 600, saldo: 600, descripcion: 'Pedido #CP-80200' }, { id: 'PM-172', fecha: '2026-02-10', tipo: 'bono', cantidad: 800, saldo: 1400, descripcion: 'Bono Partner anual' }, { id: 'PM-173', fecha: '2026-05-02', tipo: 'compra', cantidad: 400, saldo: 1800, descripcion: 'Pedido #CP-90250' }] },
    { id: 'CP-10000019', nombre: 'Lucía Salinas',      email: 'lucia.sal@yahoo.com',  telefono: '5540414243', tipo: 'normal',  puntos: 75,   fechaRegistro: '2026-08-22', totalCompras: 750,   pedidos: 2,  historialPuntos: [{ id: 'PM-181', fecha: '2026-08-23', tipo: 'compra', cantidad: 75,  saldo: 75,  descripcion: 'Pedido #CP-90260' }] },
    { id: 'CP-10000020', nombre: 'Óscar Domínguez',    email: 'oscar.d@gmail.com',    telefono: '5544454647', tipo: 'aliado',  puntos: 290,  fechaRegistro: '2026-04-05', totalCompras: 2900,  pedidos: 5,  vendedorId: 'VEN-004', historialPuntos: [{ id: 'PM-191', fecha: '2026-04-06', tipo: 'compra', cantidad: 290, saldo: 290, descripcion: 'Pedido #CP-90270' }] },
    { id: 'CP-10000021', nombre: 'Elena Rojas',        email: 'elena.r@hotmail.com',  telefono: '5548495051', tipo: 'normal',  puntos: 55,   fechaRegistro: '2026-11-03', totalCompras: 550,   pedidos: 1,  historialPuntos: [{ id: 'PM-201', fecha: '2026-11-04', tipo: 'compra', cantidad: 55,  saldo: 55,  descripcion: 'Pedido #CP-90280' }] },
    { id: 'CP-10000022', nombre: 'Ricardo Fuentes',    email: 'rfuentes@gmail.com',   telefono: '5552535455', tipo: 'partner', puntos: 4200, fechaRegistro: '2024-09-01', totalCompras: 42000, pedidos: 58, vendedorId: 'VEN-001', clave: 'RFT-2024-05', historialPuntos: [{ id: 'PM-211', fecha: '2024-10-01', tipo: 'compra', cantidad: 1000, saldo: 1000, descripcion: 'Pedido #CP-70010' }, { id: 'PM-212', fecha: '2025-03-15', tipo: 'bono', cantidad: 1500, saldo: 2500, descripcion: 'Bono volumen anual' }, { id: 'PM-213', fecha: '2025-09-20', tipo: 'compra', cantidad: 900, saldo: 3400, descripcion: 'Pedido #CP-80300' }, { id: 'PM-214', fecha: '2026-04-30', tipo: 'compra', cantidad: 800, saldo: 4200, descripcion: 'Pedido #CP-90290' }] },
    { id: 'CP-10000023', nombre: 'Gabriela Torres',    email: 'gabytorres@gmail.com', telefono: '5556575859', tipo: 'normal',  puntos: 110,  fechaRegistro: '2026-07-07', totalCompras: 1100,  pedidos: 2,  historialPuntos: [{ id: 'PM-221', fecha: '2026-07-08', tipo: 'compra', cantidad: 110, saldo: 110, descripcion: 'Pedido #CP-90300' }] },
  ];

  readonly pedidos: Pedido[] = [
    {
      id: 'CP-924501', clienteNombre: 'Ana García López', clienteId: 'CP-10000001',
      fecha: '2026-05-14T16:32:00', subtotal: 699, pointsUsed: 100, total: 599,
      status: 'pendiente', metodoPago: 'transferencia',
      items: [{ nombre: 'Audifonos Monster XKT20', qty: 1, precio: 299, color: 'Negro' }, { nombre: 'Cargador USB-C 20W', qty: 2, precio: 99, color: 'Blanco' }],
    },
    {
      id: 'CP-924498', clienteNombre: 'Roberto Jiménez', clienteId: 'CP-10000006',
      fecha: '2026-05-14T14:10:00', subtotal: 1198, pointsUsed: 0, total: 1198,
      status: 'confirmado', metodoPago: 'tarjeta',
      items: [{ nombre: 'Smart Watch Moreka GT5', qty: 2, precio: 449, color: 'Negro' }, { nombre: 'Cable Lightning 1M', qty: 4, precio: 55, color: 'Negro' }],
    },
    {
      id: 'CP-924490', clienteNombre: 'Carlos Martínez', clienteId: 'CP-10000002',
      fecha: '2026-05-13T10:45:00', subtotal: 399, pointsUsed: 50, total: 349,
      status: 'enviado', metodoPago: 'efectivo',
      items: [{ nombre: 'Control Gamer Retro', qty: 1, precio: 299, color: 'Morado' }],
      direccionEnvio: 'Calle Morelos 45, Col. Centro, Guadalajara, Jal., CP 44100',
      paqueteria: 'DHL', numeroGuia: '1234567890',
    },
    {
      id: 'CP-924478', clienteNombre: 'José Hernández', clienteId: 'CP-10000004',
      fecha: '2026-05-12T09:20:00', subtotal: 918, pointsUsed: 0, total: 918,
      status: 'entregado', metodoPago: 'transferencia',
      items: [{ nombre: 'Bocina Bluetooth RGB', qty: 2, precio: 349, color: 'Negro' }, { nombre: 'Power Bank 10000mAh', qty: 1, precio: 199 }],
      devolucionId: 'DEV-001',
    },
    {
      id: 'CP-924460', clienteNombre: 'María Rodríguez', clienteId: 'CP-10000003',
      fecha: '2026-05-11T15:00:00', subtotal: 249, pointsUsed: 0, total: 249,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Cargador Inalámbrico 15W', qty: 1, precio: 179 }],
      devolucionId: 'DEV-002',
    },
    {
      id: 'CP-924450', clienteNombre: 'Miguel Torres', clienteId: 'CP-10000008',
      fecha: '2026-05-10T11:30:00', subtotal: 557, pointsUsed: 200, total: 357,
      status: 'cancelado', metodoPago: 'tarjeta',
      items: [{ nombre: 'Diadema Moreka E72', qty: 1, precio: 199, color: 'Azul marino' }, { nombre: 'Cable Lightning 1M', qty: 2, precio: 55 }],
    },
    {
      id: 'CP-924440', clienteNombre: 'Alejandro Castro', clienteId: 'CP-10000012',
      fecha: '2026-05-09T13:00:00', subtotal: 1198, pointsUsed: 0, total: 1198,
      status: 'entregado', metodoPago: 'transferencia',
      items: [{ nombre: 'Smart Watch Moreka GT5', qty: 2, precio: 449, color: 'Negro' }, { nombre: 'Cable Lightning 1M', qty: 3, precio: 55, color: 'Negro' }],
    },
    {
      id: 'CP-924430', clienteNombre: 'Fernando López', clienteId: 'CP-10000010',
      fecha: '2026-05-08T10:15:00', subtotal: 748, pointsUsed: 0, total: 748,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Bocina Bluetooth RGB', qty: 1, precio: 459, color: 'Azul' }, { nombre: 'Power Bank 10000mAh', qty: 1, precio: 299, color: 'Negro' }],
    },
    {
      id: 'CP-924420', clienteNombre: 'Diego Mendoza', clienteId: 'CP-10000016',
      fecha: '2026-05-07T16:45:00', subtotal: 499, pointsUsed: 100, total: 399,
      status: 'enviado', metodoPago: 'tarjeta',
      items: [{ nombre: 'Control Gamer Retro', qty: 1, precio: 399, color: 'Negro' }, { nombre: 'Cable Lightning 1M', qty: 1, precio: 89, color: 'Blanco' }],
    },
    {
      id: 'CP-924410', clienteNombre: 'Andrés Navarro', clienteId: 'CP-10000018',
      fecha: '2026-05-06T09:30:00', subtotal: 1797, pointsUsed: 0, total: 1797,
      status: 'entregado', metodoPago: 'transferencia',
      items: [{ nombre: 'Samsung Galaxy A55 5G', qty: 1, precio: 8999 }],
    },
    {
      id: 'CP-924400', clienteNombre: 'Sofía Pérez', clienteId: 'CP-10000011',
      fecha: '2026-05-05T14:20:00', subtotal: 347, pointsUsed: 0, total: 347,
      status: 'pendiente', metodoPago: 'efectivo',
      items: [{ nombre: 'Cargador USB-C 20W', qty: 2, precio: 149, color: 'Blanco' }, { nombre: 'Cable Lightning 1M', qty: 1, precio: 89, color: 'Negro' }],
    },
    {
      id: 'CP-924390', clienteNombre: 'Ricardo Fuentes', clienteId: 'CP-10000022',
      fecha: '2026-05-04T11:00:00', subtotal: 2196, pointsUsed: 500, total: 1696,
      status: 'entregado', metodoPago: 'tarjeta',
      items: [{ nombre: 'Bocina Bluetooth RGB', qty: 3, precio: 459, color: 'Negro' }, { nombre: 'Power Bank 10000mAh', qty: 2, precio: 299, color: 'Blanco' }],
    },
    {
      id: 'CP-924380', clienteNombre: 'Ana García López', clienteId: 'CP-10000001',
      fecha: '2026-05-03T15:40:00', subtotal: 598, pointsUsed: 0, total: 598,
      status: 'confirmado', metodoPago: 'transferencia',
      items: [{ nombre: 'Audifonos Monster XKT20', qty: 1, precio: 349, color: 'Blanco' }, { nombre: 'Cargador Inalámbrico 15W', qty: 1, precio: 249, color: 'Blanco' }],
    },
    {
      id: 'CP-924370', clienteNombre: 'Eduardo Ramírez', clienteId: 'CP-10000014',
      fecha: '2026-05-02T08:55:00', subtotal: 289, pointsUsed: 0, total: 289,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Diadema Moreka E72', qty: 1, precio: 289, color: 'Negro' }],
    },
    {
      id: 'CP-924360', clienteNombre: 'Roberto Jiménez', clienteId: 'CP-10000006',
      fecha: '2026-05-01T17:10:00', subtotal: 3592, pointsUsed: 0, total: 3592,
      status: 'entregado', metodoPago: 'tarjeta',
      items: [{ nombre: 'Xiaomi Redmi Note 13', qty: 1, precio: 4499 }, { nombre: 'Smart Watch Moreka GT5', qty: 1, precio: 599, color: 'Plata' }],
    },
    {
      id: 'CP-924350', clienteNombre: 'Camila Herrera', clienteId: 'CP-10000017',
      fecha: '2026-04-30T12:25:00', subtotal: 249, pointsUsed: 0, total: 249,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Power Bank 10000mAh', qty: 1, precio: 299, color: 'Negro' }],
    },
    {
      id: 'CP-924340', clienteNombre: 'Óscar Domínguez', clienteId: 'CP-10000020',
      fecha: '2026-04-29T10:05:00', subtotal: 399, pointsUsed: 0, total: 399,
      status: 'enviado', metodoPago: 'transferencia',
      items: [{ nombre: 'Bocina Bluetooth RGB', qty: 1, precio: 459, color: 'Verde' }],
    },
    {
      id: 'CP-924330', clienteNombre: 'José Hernández', clienteId: 'CP-10000004',
      fecha: '2026-04-28T14:50:00', subtotal: 697, pointsUsed: 0, total: 697,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Audifonos Monster XKT20', qty: 2, precio: 349, color: 'Negro' }],
    },
    {
      id: 'CP-924320', clienteNombre: 'Natalia Guzmán', clienteId: 'CP-10000015',
      fecha: '2026-04-27T09:15:00', subtotal: 948, pointsUsed: 0, total: 948,
      status: 'pendiente', metodoPago: 'tarjeta',
      items: [{ nombre: 'Cargador Inalámbrico 15W', qty: 2, precio: 249, color: 'Blanco' }, { nombre: 'Cable Lightning 1M', qty: 5, precio: 89, color: 'Negro' }],
    },
    {
      id: 'CP-924310', clienteNombre: 'Alejandro Castro', clienteId: 'CP-10000012',
      fecha: '2026-04-26T16:00:00', subtotal: 1597, pointsUsed: 300, total: 1297,
      status: 'entregado', metodoPago: 'transferencia',
      items: [{ nombre: 'Smart Watch Moreka GT5', qty: 2, precio: 599, color: 'Negro' }, { nombre: 'Power Bank 10000mAh', qty: 2, precio: 299, color: 'Negro' }],
    },
    {
      id: 'CP-924300', clienteNombre: 'Lucía Salinas', clienteId: 'CP-10000019',
      fecha: '2026-04-25T11:30:00', subtotal: 249, pointsUsed: 0, total: 249,
      status: 'entregado', metodoPago: 'efectivo',
      items: [{ nombre: 'Cargador USB-C 20W', qty: 1, precio: 149, color: 'Negro' }, { nombre: 'Cable Lightning 1M', qty: 1, precio: 89, color: 'Blanco' }],
    },
    {
      id: 'CP-924290', clienteNombre: 'Ana García López', clienteId: 'CP-10000001',
      fecha: '2026-04-24T13:15:00', subtotal: 996, pointsUsed: 0, total: 996,
      status: 'entregado', metodoPago: 'transferencia',
      items: [
        { nombre: 'Playera Planet Sport', qty: 2, precio: 249, color: 'Negro / M' },
        { nombre: 'Playera Planet Sport', qty: 1, precio: 249, color: 'Blanco / L' },
        { nombre: 'Chamarra Softshell Táctica', qty: 1, precio: 749, color: 'Negro / XL' },
      ],
    },
    {
      id: 'CP-924280', clienteNombre: 'Roberto Jiménez', clienteId: 'CP-10000006',
      fecha: '2026-04-23T10:00:00', subtotal: 319, pointsUsed: 0, total: 319,
      status: 'pendiente', metodoPago: 'efectivo',
      items: [
        { nombre: 'Papas Sabritas 45g', qty: 5, precio: 22, color: 'Jalapeño' },
        { nombre: 'Papas Sabritas 45g', qty: 3, precio: 22, color: 'Sal y Limón' },
        { nombre: 'Agua Epura 600ml',   qty: 6, precio: 15 },
        { nombre: 'Samsung Galaxy A55 5G', qty: 1, precio: 8999, color: 'Azul / 128GB' },
      ],
    },
  ];

  devoluciones: Devolucion[] = [
    {
      id: 'DEV-001', pedidoId: 'CP-924478', clienteId: 'CP-10000004',
      clienteNombre: 'José Hernández', fecha: '2026-05-15',
      status: 'solicitada',
      items: [{ nombre: 'Bocina Bluetooth RGB', qty: 1, precio: 349, color: 'Negro' }],
      motivo: 'Producto defectuoso al abrir la caja, no enciende.',
      reembolsoTotal: 349,
    },
    {
      id: 'DEV-002', pedidoId: 'CP-924460', clienteId: 'CP-10000003',
      clienteNombre: 'María Rodríguez', fecha: '2026-05-13',
      status: 'aprobada',
      items: [{ nombre: 'Cargador Inalámbrico 15W', qty: 1, precio: 179 }],
      motivo: 'El cargador no era compatible con su dispositivo.',
      notas: 'Cliente trae el producto en caja original con todos los accesorios.',
      reembolsoTotal: 179,
    },
  ];

  readonly productos: Producto[] = [
    { id: 1,  nombre: 'Audifonos Monster XKT20',   marca: 'Monster',  categoria: 'audifonos',   descripcion: 'Audífonos inalámbricos con cancelación de ruido activa y 40 horas de batería. Bluetooth 5.3, driver de 40mm.', precio: 349, precioAliado: 299, precioPartner: 249, costo: 185, stock: 12, emoji: '🎧', badge: 'POPULAR', activo: true,  colores: ['Negro', 'Blanco', 'Rojo'],        manejaPorColor: true },
    { id: 2,  nombre: 'Diadema Moreka E72',         marca: 'Moreka',   categoria: 'diademas',    descripcion: 'Diadema over-ear con sonido surround 7.1 y almohadillas de memory foam. Ideal para gaming.', precio: 289, precioAliado: 249, precioPartner: 199, costo: 140, stock: 3,  emoji: '🎧', badge: 'NUEVO',   activo: true,  colores: ['Negro', 'Azul marino'],           manejaPorColor: true },
    { id: 3,  nombre: 'Cargador USB-C 20W',         marca: 'Genérico', categoria: 'cargadores',  descripcion: 'Cargador de carga rápida PD 20W compatible con iPhone y Android. Compacto y certificado CE.', precio: 149, precioAliado: 129, precioPartner: 99,  costo: 55,  stock: 25, emoji: '🔌', activo: true,  colores: ['Blanco', 'Negro'],                manejaPorColor: true },
    { id: 4,  nombre: 'Smart Watch Moreka GT5',     marca: 'Moreka',   categoria: 'smartwatch',  descripcion: 'Smartwatch con monitor cardíaco, GPS integrado y pantalla AMOLED 1.4". Resistencia al agua IP68.', precio: 599, precioAliado: 519, precioPartner: 449, costo: 290, stock: 2,  emoji: '⌚', badge: 'OFERTA',  activo: true,  colores: ['Negro', 'Plata', 'Rose Gold'],    manejaPorColor: true },
    { id: 5,  nombre: 'Bocina Bluetooth RGB',       marca: 'Genérico', categoria: 'bocinas',     descripcion: 'Bocina portátil con iluminación RGB, 12W de potencia y batería de 2000mAh para 8 horas de uso.', precio: 459, precioAliado: 399, precioPartner: 349, costo: 210, stock: 8,  emoji: '🔊', activo: true,  colores: ['Negro', 'Azul', 'Verde'],         manejaPorColor: true },
    { id: 6,  nombre: 'Power Bank 10000mAh',        marca: 'Genérico', categoria: 'powerbank',   descripcion: 'Batería portátil de 10000mAh con dos puertos USB-A y una entrada USB-C. Carga rápida 18W.', precio: 299, precioAliado: 249, precioPartner: 199, costo: 130, stock: 15, emoji: '🔋', badge: 'OFERTA',  activo: true,  colores: ['Negro', 'Blanco'],                manejaPorColor: true },
    { id: 7,  nombre: 'Cable Lightning 1M',         marca: 'Genérico', categoria: 'cables',      descripcion: 'Cable Lightning certificado MFi, nylon trenzado resistente a dobleces. Compatible con todos los iPhone.', precio: 89, precioAliado: 69, precioPartner: 55, costo: 28, stock: 40, emoji: '🔗', activo: true,  colores: ['Negro', 'Blanco'],                manejaPorColor: true },
    { id: 8,  nombre: 'Control Gamer Retro',        marca: 'Genérico', categoria: 'videojuegos', descripcion: 'Control inalámbrico estilo retro compatible con PC, Android y consolas vía Bluetooth 5.0.', precio: 399, precioAliado: 349, precioPartner: 299, costo: 180, stock: 6,  emoji: '🎮', badge: 'NUEVO',   activo: true,  colores: ['Negro', 'Morado', 'Blanco'],      manejaPorColor: true },
    { id: 9,  nombre: 'Audifono In-Ear TWS',        marca: 'Genérico', categoria: 'audifonos',   descripcion: 'Audífonos TWS con estuche de carga inalámbrica, 6 horas de reproducción y control táctil.', precio: 199, precioAliado: 169, precioPartner: 139, costo: 80, stock: 0, emoji: '🎵', badge: 'OFERTA', activo: false, colores: ['Blanco', 'Negro'],                manejaPorColor: true },
    { id: 10, nombre: 'Cargador Inalámbrico 15W',   marca: 'Genérico', categoria: 'cargadores',  descripcion: 'Cargador inalámbrico Qi 15W compatible con iPhone MagSafe y Android. Base antideslizante.', precio: 249, precioAliado: 209, precioPartner: 179, costo: 95,  stock: 18, emoji: '⚡', activo: true,  colores: ['Blanco', 'Negro'],                manejaPorColor: true },

    // ── Ropa ──────────────────────────────────────────────────────────────────
    { id: 11, nombre: 'Playera Planet Sport',        marca: 'Planet',   categoria: 'ropa',        descripcion: 'Playera de algodón 100% con corte slim fit y cuello redondo. Serigrafía con logo Planet en el pecho. Lavable a máquina.', precio: 249, precioAliado: 199, precioPartner: 169, costo: 95,  stock: 0,  emoji: '👕', badge: 'NUEVO',  activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Blanco', 'Gris'] }, { dim: 'Talla', valores: ['S', 'M', 'L', 'XL'] }] },
    { id: 12, nombre: 'Chamarra Softshell Táctica',  marca: 'Planet',   categoria: 'ropa',        descripcion: 'Chamarra impermeable con forro polar interior, bolsillos con cierre y capucha desmontable. Perfecta para exteriores.', precio: 749, precioAliado: 649, precioPartner: 549, costo: 320, stock: 0,  emoji: '🧥', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Verde militar', 'Azul marino'] }, { dim: 'Talla', valores: ['M', 'L', 'XL', 'XXL'] }] },

    // ── Electrónica / Celulares ───────────────────────────────────────────────
    { id: 13, nombre: 'Samsung Galaxy A55 5G',       marca: 'Samsung',  categoria: 'celulares',   descripcion: 'Celular Samsung Galaxy A55 5G con pantalla Super AMOLED 6.6", cámara principal de 50MP, batería 5000mAh y carga rápida 25W.', precio: 8999, precioAliado: 7999, precioPartner: 6999, costo: 5800, stock: 0, emoji: '📱', badge: 'NUEVO', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Azul', 'Lila'] }, { dim: 'Almacenamiento', valores: ['128GB', '256GB'] }] },
    { id: 14, nombre: 'Xiaomi Redmi Note 13',        marca: 'Xiaomi',   categoria: 'celulares',   descripcion: 'Redmi Note 13 con pantalla AMOLED 6.67" 120Hz, cámara 108MP, Snapdragon 685 y batería 5000mAh con carga rápida 33W.', precio: 4499, precioAliado: 3999, precioPartner: 3499, costo: 2900, stock: 0, emoji: '📱', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Verde menta', 'Azul cielo'] }, { dim: 'Almacenamiento', valores: ['128GB', '256GB'] }] },

    // ── Juguetes ──────────────────────────────────────────────────────────────
    { id: 15, nombre: 'Cubo Rubik Speed 3x3',        marca: 'MoYu',     categoria: 'juguetes',    descripcion: 'Cubo de velocidad 3x3 con mecanismo magnético, lubricado de fábrica. Estructura sin pegatinas, resistente a caídas.', precio: 179, precioAliado: 149, precioPartner: 129, costo: 70,  stock: 0,  emoji: '🧩', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Blanco'] }] },
    { id: 16, nombre: 'Spinner LED RGB',              marca: 'Genérico', categoria: 'juguetes',    descripcion: 'Spinner con luces LED RGB de 11 colores, batería recargable USB y hasta 3 minutos de giro continuo. Ideal para aliviar el estrés.', precio: 99, precioAliado: 79, precioPartner: 65, costo: 35, stock: 0, emoji: '💫', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Negro', 'Rojo', 'Azul'] }] },

    // ── Hogar ─────────────────────────────────────────────────────────────────
    { id: 17, nombre: 'Foco LED WiFi 9W',             marca: 'Smart+',   categoria: 'hogar',       descripcion: 'Foco inteligente WiFi 2.4GHz, 9W equivalente a 60W, luz regulable de cálida a blanca, compatible con Alexa y Google Home.', precio: 199, precioAliado: 169, precioPartner: 149, costo: 88,  stock: 0,  emoji: '💡', activo: true,  manejaPorColor: false },
    { id: 18, nombre: 'Tapete Antifatiga Premium',    marca: 'Genérico', categoria: 'hogar',       descripcion: 'Tapete antifatiga de espuma memory foam 45×75cm, superficie antideslizante lavable, ideal para cocina o escritorio.', precio: 299, precioAliado: 249, precioPartner: 219, costo: 130, stock: 0,  emoji: '🟫', activo: true,
      atributosVariante: [{ dim: 'Color', valores: ['Gris', 'Negro', 'Beige'] }] },

    // ── Alimentos ─────────────────────────────────────────────────────────────
    { id: 19, nombre: 'Papas Sabritas 45g',           marca: 'Sabritas', categoria: 'alimentos',   descripcion: 'Papas fritas saborizadas en presentación individual de 45g. Producto de botana para reventa.', precio: 22, precioAliado: 18, precioPartner: 15, costo: 9, stock: 0, emoji: '🍿', activo: true,
      atributosVariante: [{ dim: 'Sabor', valores: ['Original', 'Sal y Limón', 'Jalapeño', 'Queso'] }] },
    { id: 20, nombre: 'Agua Epura 600ml',             marca: 'Bonafont', categoria: 'alimentos',   descripcion: 'Agua purificada en botella PET de 600ml. Presentación individual para reventa en punto de venta.', precio: 15, precioAliado: 12, precioPartner: 10, costo: 6, stock: 0, emoji: '💧', activo: true,  manejaPorColor: false },
  ];

  sucursales: Sucursal[] = [
    { id: 'SUC-01', nombre: 'Sucursal Centro',    tipo: 'fijo',  direccion: 'Av. Juárez 123, Centro' },
    { id: 'SUC-02', nombre: 'Sucursal Norte',     tipo: 'fijo',  direccion: 'Blvd. Norte 456, Col. Industrial' },
    { id: 'MOV-01', nombre: 'Vendedor Luis',      tipo: 'movil', direccion: 'Zona Mercados' },
    { id: 'MOV-02', nombre: 'Vendedor Carmen',    tipo: 'movil', direccion: 'Zona Plazas' },
  ];

  readonly inventario: InventarioItem[] = [
    // Prod 1 – Audifonos Monster XKT20 (Negro / Blanco / Rojo) — total 12
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-01', color: 'Negro',      stock: 2 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-01', color: 'Blanco',     stock: 2 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-01', color: 'Rojo',       stock: 1 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-02', color: 'Negro',      stock: 2 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-02', color: 'Blanco',     stock: 1 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'SUC-02', color: 'Rojo',       stock: 1 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'MOV-01', color: 'Negro',      stock: 1 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'MOV-01', color: 'Blanco',     stock: 1 },
    { productoId: 1, productoNombre: 'Audifonos Monster XKT20', sucursalId: 'MOV-02', color: 'Negro',      stock: 1 },
    // Prod 2 – Diadema Moreka E72 (Negro / Azul marino) — total 3
    { productoId: 2, productoNombre: 'Diadema Moreka E72',      sucursalId: 'SUC-01', color: 'Negro',      stock: 1 },
    { productoId: 2, productoNombre: 'Diadema Moreka E72',      sucursalId: 'SUC-01', color: 'Azul marino',stock: 1 },
    { productoId: 2, productoNombre: 'Diadema Moreka E72',      sucursalId: 'SUC-02', color: 'Negro',      stock: 1 },
    // Prod 3 – Cargador USB-C 20W (Blanco / Negro) — total 25
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'SUC-01', color: 'Blanco',     stock: 6 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'SUC-01', color: 'Negro',      stock: 4 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'SUC-02', color: 'Blanco',     stock: 5 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'SUC-02', color: 'Negro',      stock: 3 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'MOV-01', color: 'Blanco',     stock: 2 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'MOV-01', color: 'Negro',      stock: 2 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'MOV-02', color: 'Blanco',     stock: 2 },
    { productoId: 3, productoNombre: 'Cargador USB-C 20W',      sucursalId: 'MOV-02', color: 'Negro',      stock: 1 },
    // Prod 4 – Smart Watch Moreka GT5 (Negro / Plata / Rose Gold) — total 2
    { productoId: 4, productoNombre: 'Smart Watch Moreka GT5',  sucursalId: 'SUC-01', color: 'Negro',      stock: 1 },
    { productoId: 4, productoNombre: 'Smart Watch Moreka GT5',  sucursalId: 'SUC-02', color: 'Plata',      stock: 1 },
    // Prod 5 – Bocina Bluetooth RGB (Negro / Azul / Verde) — total 8
    { productoId: 5, productoNombre: 'Bocina Bluetooth RGB',    sucursalId: 'SUC-01', color: 'Negro',      stock: 3 },
    { productoId: 5, productoNombre: 'Bocina Bluetooth RGB',    sucursalId: 'SUC-01', color: 'Azul',       stock: 1 },
    { productoId: 5, productoNombre: 'Bocina Bluetooth RGB',    sucursalId: 'SUC-02', color: 'Negro',      stock: 2 },
    { productoId: 5, productoNombre: 'Bocina Bluetooth RGB',    sucursalId: 'SUC-02', color: 'Verde',      stock: 1 },
    { productoId: 5, productoNombre: 'Bocina Bluetooth RGB',    sucursalId: 'MOV-01', color: 'Negro',      stock: 1 },
    // Prod 6 – Power Bank 10000mAh (Negro / Blanco) — total 15
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'SUC-01', color: 'Negro',      stock: 5 },
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'SUC-01', color: 'Blanco',     stock: 3 },
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'SUC-02', color: 'Negro',      stock: 3 },
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'SUC-02', color: 'Blanco',     stock: 2 },
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'MOV-01', color: 'Negro',      stock: 1 },
    { productoId: 6, productoNombre: 'Power Bank 10000mAh',     sucursalId: 'MOV-01', color: 'Blanco',     stock: 1 },
    // Prod 7 – Cable Lightning 1M (Negro / Blanco) — total 40
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'SUC-01', color: 'Negro',      stock: 12 },
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'SUC-01', color: 'Blanco',     stock: 8 },
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'MOV-01', color: 'Negro',      stock: 5 },
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'MOV-01', color: 'Blanco',     stock: 5 },
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'MOV-02', color: 'Negro',      stock: 6 },
    { productoId: 7, productoNombre: 'Cable Lightning 1M',      sucursalId: 'MOV-02', color: 'Blanco',     stock: 4 },
    // Prod 8 – Control Gamer Retro (Negro / Morado / Blanco) — total 6
    { productoId: 8, productoNombre: 'Control Gamer Retro',     sucursalId: 'SUC-01', color: 'Negro',      stock: 2 },
    { productoId: 8, productoNombre: 'Control Gamer Retro',     sucursalId: 'SUC-01', color: 'Morado',     stock: 1 },
    { productoId: 8, productoNombre: 'Control Gamer Retro',     sucursalId: 'SUC-02', color: 'Negro',      stock: 1 },
    { productoId: 8, productoNombre: 'Control Gamer Retro',     sucursalId: 'SUC-02', color: 'Blanco',     stock: 1 },
    { productoId: 8, productoNombre: 'Control Gamer Retro',     sucursalId: 'MOV-01', color: 'Morado',     stock: 1 },
    // Prod 10 – Cargador Inalámbrico 15W (Blanco / Negro) — total 18
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'SUC-01', color: 'Blanco',  stock: 6 },
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'SUC-01', color: 'Negro',   stock: 3 },
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'SUC-02', color: 'Blanco',  stock: 4 },
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'SUC-02', color: 'Negro',   stock: 2 },
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'MOV-01', color: 'Blanco',  stock: 2 },
    { productoId: 10, productoNombre: 'Cargador Inalámbrico 15W', sucursalId: 'MOV-01', color: 'Negro',   stock: 1 },

    // Prod 11 – Playera Planet Sport (Color / Talla)
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Negro / S',           stock: 3 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Negro / M',           stock: 6 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Negro / L',           stock: 5 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Negro / XL',          stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Blanco / M',          stock: 4 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Blanco / L',          stock: 3 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Gris / M',            stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-01', color: 'Gris / L',            stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-02', color: 'Negro / M',           stock: 4 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-02', color: 'Negro / L',           stock: 3 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-02', color: 'Blanco / M',          stock: 3 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'SUC-02', color: 'Blanco / L',          stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'MOV-01', color: 'Negro / M',           stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'MOV-01', color: 'Negro / L',           stock: 2 },
    { productoId: 11, productoNombre: 'Playera Planet Sport',       sucursalId: 'MOV-01', color: 'Blanco / M',          stock: 1 },

    // Prod 12 – Chamarra Softshell (Color / Talla)
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-01', color: 'Negro / M',           stock: 3 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-01', color: 'Negro / L',           stock: 4 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-01', color: 'Negro / XL',          stock: 2 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-01', color: 'Verde militar / M',   stock: 2 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-01', color: 'Verde militar / L',   stock: 3 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-02', color: 'Negro / L',           stock: 2 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-02', color: 'Negro / XL',          stock: 1 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-02', color: 'Verde militar / L',   stock: 2 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-02', color: 'Azul marino / M',     stock: 2 },
    { productoId: 12, productoNombre: 'Chamarra Softshell Táctica', sucursalId: 'SUC-02', color: 'Azul marino / L',     stock: 1 },

    // Prod 13 – Samsung Galaxy A55 5G (Color / Almacenamiento)
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-01', color: 'Negro / 128GB',       stock: 3 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-01', color: 'Negro / 256GB',       stock: 2 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-01', color: 'Azul / 128GB',        stock: 2 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-01', color: 'Lila / 128GB',        stock: 1 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-02', color: 'Negro / 128GB',       stock: 2 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'SUC-02', color: 'Azul / 256GB',        stock: 1 },
    { productoId: 13, productoNombre: 'Samsung Galaxy A55 5G',      sucursalId: 'MOV-01', color: 'Negro / 128GB',       stock: 1 },

    // Prod 14 – Xiaomi Redmi Note 13 (Color / Almacenamiento)
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-01', color: 'Negro / 128GB',       stock: 4 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-01', color: 'Negro / 256GB',       stock: 2 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-01', color: 'Verde menta / 128GB', stock: 3 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-01', color: 'Azul cielo / 128GB',  stock: 2 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-02', color: 'Negro / 128GB',       stock: 3 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'SUC-02', color: 'Verde menta / 256GB', stock: 1 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'MOV-01', color: 'Negro / 128GB',       stock: 2 },
    { productoId: 14, productoNombre: 'Xiaomi Redmi Note 13',       sucursalId: 'MOV-02', color: 'Negro / 128GB',       stock: 1 },

    // Prod 15 – Cubo Rubik Speed 3x3 (Color)
    { productoId: 15, productoNombre: 'Cubo Rubik Speed 3x3',       sucursalId: 'SUC-01', color: 'Negro',               stock: 8 },
    { productoId: 15, productoNombre: 'Cubo Rubik Speed 3x3',       sucursalId: 'SUC-01', color: 'Blanco',              stock: 4 },
    { productoId: 15, productoNombre: 'Cubo Rubik Speed 3x3',       sucursalId: 'SUC-02', color: 'Negro',               stock: 5 },
    { productoId: 15, productoNombre: 'Cubo Rubik Speed 3x3',       sucursalId: 'SUC-02', color: 'Blanco',              stock: 3 },
    { productoId: 15, productoNombre: 'Cubo Rubik Speed 3x3',       sucursalId: 'MOV-01', color: 'Negro',               stock: 3 },

    // Prod 16 – Spinner LED RGB (Color)
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'SUC-01', color: 'Negro',              stock: 10 },
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'SUC-01', color: 'Rojo',               stock: 6 },
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'SUC-01', color: 'Azul',               stock: 8 },
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'MOV-01', color: 'Negro',              stock: 4 },
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'MOV-01', color: 'Rojo',               stock: 3 },
    { productoId: 16, productoNombre: 'Spinner LED RGB',             sucursalId: 'MOV-01', color: 'Azul',               stock: 4 },

    // Prod 17 – Foco LED WiFi (sin variante)
    { productoId: 17, productoNombre: 'Foco LED WiFi 9W',            sucursalId: 'SUC-01', stock: 12 },
    { productoId: 17, productoNombre: 'Foco LED WiFi 9W',            sucursalId: 'SUC-02', stock: 8 },
    { productoId: 17, productoNombre: 'Foco LED WiFi 9W',            sucursalId: 'MOV-02', stock: 4 },

    // Prod 18 – Tapete Antifatiga (Color)
    { productoId: 18, productoNombre: 'Tapete Antifatiga Premium',   sucursalId: 'SUC-01', color: 'Gris',               stock: 5 },
    { productoId: 18, productoNombre: 'Tapete Antifatiga Premium',   sucursalId: 'SUC-01', color: 'Negro',              stock: 4 },
    { productoId: 18, productoNombre: 'Tapete Antifatiga Premium',   sucursalId: 'SUC-01', color: 'Beige',              stock: 3 },
    { productoId: 18, productoNombre: 'Tapete Antifatiga Premium',   sucursalId: 'SUC-02', color: 'Gris',               stock: 4 },
    { productoId: 18, productoNombre: 'Tapete Antifatiga Premium',   sucursalId: 'SUC-02', color: 'Negro',              stock: 2 },

    // Prod 19 – Papas Sabritas (Sabor — sin punto de color)
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-01', color: 'Original',           stock: 20 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-01', color: 'Sal y Limón',        stock: 15 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-01', color: 'Jalapeño',           stock: 12 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-01', color: 'Queso',              stock: 10 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-02', color: 'Original',           stock: 15 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-02', color: 'Sal y Limón',        stock: 10 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'SUC-02', color: 'Jalapeño',           stock: 8 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'MOV-01', color: 'Original',           stock: 10 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'MOV-01', color: 'Sal y Limón',        stock: 8 },
    { productoId: 19, productoNombre: 'Papas Sabritas 45g',          sucursalId: 'MOV-01', color: 'Jalapeño',           stock: 5 },

    // Prod 20 – Agua Epura 600ml (sin variante)
    { productoId: 20, productoNombre: 'Agua Epura 600ml',            sucursalId: 'SUC-01', stock: 50 },
    { productoId: 20, productoNombre: 'Agua Epura 600ml',            sucursalId: 'SUC-02', stock: 35 },
    { productoId: 20, productoNombre: 'Agua Epura 600ml',            sucursalId: 'MOV-01', stock: 20 },
    { productoId: 20, productoNombre: 'Agua Epura 600ml',            sucursalId: 'MOV-02', stock: 15 },
  ];

  readonly transferencias: Transferencia[] = [
    { id: 'TRF-001', origen: 'SUC-01', destino: 'MOV-01', productoNombre: 'Cargador USB-C 20W',     cantidad: 5, fecha: '2026-05-14T09:00:00', status: 'completado' },
    { id: 'TRF-002', origen: 'SUC-01', destino: 'MOV-02', productoNombre: 'Cable Lightning 1M',     cantidad: 10, fecha: '2026-05-13T11:00:00', status: 'completado' },
    { id: 'TRF-003', origen: 'SUC-02', destino: 'MOV-01', productoNombre: 'Audifonos Monster XKT20', cantidad: 2, fecha: '2026-05-15T08:30:00', status: 'en_transito' },
    { id: 'TRF-004', origen: 'SUC-01', destino: 'SUC-02', productoNombre: 'Smart Watch Moreka GT5', cantidad: 1, fecha: '2026-05-15T10:00:00', status: 'pendiente' },
  ];

  readonly cupones: Cupon[] = [
    { id: 'CUP-001', codigo: 'BIENVENIDO10', tipo: 'porcentaje', valor: 10,  fechaInicio: '2026-01-01', fechaFin: '2026-12-31', usoMaximo: 100, usosActuales: 34, activo: true  },
    { id: 'CUP-002', codigo: 'MAYO50',       tipo: 'fijo',       valor: 50,  fechaInicio: '2026-05-01', fechaFin: '2026-05-31', usoMaximo: 50,  usosActuales: 22, activo: true  },
    { id: 'CUP-003', codigo: 'ALIADO15',     tipo: 'porcentaje', valor: 15,  fechaInicio: '2026-04-01', fechaFin: '2026-06-30', usoMaximo: 200, usosActuales: 87, activo: true,  tiposCliente: ['aliado'] },
    { id: 'CUP-004', codigo: 'HOT2024',      tipo: 'fijo',       valor: 100, fechaInicio: '2024-11-01', fechaFin: '2024-12-31', usoMaximo: 30,  usosActuales: 30, activo: false },
    { id: 'CUP-005', codigo: 'PARTNER20',    tipo: 'porcentaje', valor: 20,  fechaInicio: '2026-05-01', fechaFin: '2026-07-31', usoMaximo: 50,  usosActuales: 8,  activo: true,  tiposCliente: ['partner'] },
    { id: 'CUP-006', codigo: 'VIPSUMMER',    tipo: 'porcentaje', valor: 12,  fechaInicio: '2026-06-01', fechaFin: '2026-08-31', usoMaximo: 80,  usosActuales: 0,  activo: true,  tiposCliente: ['aliado', 'partner'] },
  ];

  categorias: CategoriaProducto[] = [
    {
      id: 'CAT-ACC', nombre: 'Accesorios celular', emoji: '📱',
      atributos: [
        { key: 'color',          label: 'Color',          tipo: 'texto',  requerido: false, esVariante: true },
        { key: 'compatibilidad', label: 'Compatibilidad', tipo: 'texto',  requerido: false, esVariante: false },
        { key: 'garantia',       label: 'Garantía',       tipo: 'select', opciones: ['3 meses','6 meses','1 año','2 años'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ROPA', nombre: 'Ropa', emoji: '👕',
      atributos: [
        { key: 'talla',    label: 'Talla',    tipo: 'select', opciones: ['XS','S','M','L','XL','XXL','XXXL'], requerido: true,  esVariante: true },
        { key: 'color',    label: 'Color',    tipo: 'texto',  requerido: false, esVariante: true },
        { key: 'material', label: 'Material', tipo: 'texto',  requerido: false, esVariante: false },
        { key: 'genero',   label: 'Género',   tipo: 'select', opciones: ['Hombre','Mujer','Unisex','Niño','Niña'], requerido: false, esVariante: false },
        { key: 'temporada',label: 'Temporada',tipo: 'select', opciones: ['Primavera/Verano','Otoño/Invierno','Todo el año'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ALIM', nombre: 'Alimentos', emoji: '🍎',
      atributos: [
        { key: 'peso',      label: 'Peso / Volumen',   tipo: 'texto',    requerido: false, esVariante: false },
        { key: 'caducidad', label: 'Fecha caducidad',  tipo: 'fecha',    requerido: false, esVariante: false },
        { key: 'vegano',    label: 'Vegano',            tipo: 'booleano', requerido: false, esVariante: false },
        { key: 'sinGluten', label: 'Sin gluten',        tipo: 'booleano', requerido: false, esVariante: false },
        { key: 'sinAzucar', label: 'Sin azúcar',        tipo: 'booleano', requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-JUG', nombre: 'Juguetes', emoji: '🧸',
      atributos: [
        { key: 'edadMin',  label: 'Edad mínima (años)', tipo: 'numero',   requerido: false, esVariante: false },
        { key: 'pilas',    label: 'Requiere pilas',      tipo: 'booleano', requerido: false, esVariante: false },
        { key: 'color',    label: 'Color',               tipo: 'texto',    requerido: false, esVariante: true },
        { key: 'material', label: 'Material',            tipo: 'texto',    requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-HOG', nombre: 'Hogar', emoji: '🏠',
      atributos: [
        { key: 'dimensiones', label: 'Dimensiones',  tipo: 'texto',  requerido: false, esVariante: false },
        { key: 'material',    label: 'Material',     tipo: 'texto',  requerido: false, esVariante: false },
        { key: 'color',       label: 'Color',        tipo: 'texto',  requerido: false, esVariante: true },
        { key: 'habitacion',  label: 'Habitación',   tipo: 'select', opciones: ['Sala','Cocina','Recámara','Baño','Exterior','General'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ELECT', nombre: 'Electrónica', emoji: '💻',
      atributos: [
        { key: 'color',          label: 'Color',          tipo: 'texto',  requerido: false, esVariante: true },
        { key: 'almacenamiento', label: 'Almacenamiento', tipo: 'select', opciones: ['64GB','128GB','256GB','512GB','1TB'], requerido: false, esVariante: true },
        { key: 'garantia',       label: 'Garantía',       tipo: 'select', opciones: ['6 meses','1 año','2 años','3 años'], requerido: false, esVariante: false },
      ],
    },
    // ── Subcategorías de Ropa ─────────────────────────────────────────────────
    {
      id: 'CAT-ROPA-MUJ', nombre: 'Mujer', emoji: '👗', parentId: 'CAT-ROPA',
      atributos: [
        { key: 'tipo_prenda', label: 'Tipo de prenda', tipo: 'select', opciones: ['Vestido','Blusa','Pantalón','Falda','Abrigo','Chamarra','Playera'], requerido: false, esVariante: false },
        { key: 'largo',       label: 'Largo',          tipo: 'select', opciones: ['Mini','Midi','Maxi','Regular'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ROPA-HOM', nombre: 'Hombre', emoji: '👔', parentId: 'CAT-ROPA',
      atributos: [
        { key: 'tipo_prenda', label: 'Tipo de prenda', tipo: 'select', opciones: ['Camisa','Playera','Pantalón','Chamarra','Abrigo','Short','Traje'], requerido: false, esVariante: false },
        { key: 'corte',       label: 'Corte',          tipo: 'select', opciones: ['Skinny','Slim','Regular','Oversize'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ROPA-NIN', nombre: 'Niños', emoji: '🧒', parentId: 'CAT-ROPA',
      atributos: [
        { key: 'edad_min', label: 'Edad mínima (años)', tipo: 'numero', requerido: false, esVariante: false },
        { key: 'edad_max', label: 'Edad máxima (años)', tipo: 'numero', requerido: false, esVariante: false },
      ],
    },
    // ── Subcategorías de Alimentos ────────────────────────────────────────────
    {
      id: 'CAT-ALIM-BEB', nombre: 'Bebidas', emoji: '🥤', parentId: 'CAT-ALIM',
      atributos: [
        { key: 'volumen',     label: 'Volumen',     tipo: 'select', opciones: ['250ml','355ml','500ml','600ml','1L','1.5L','2L','3L'], requerido: false, esVariante: true },
        { key: 'tipo_bebida', label: 'Tipo',         tipo: 'select', opciones: ['Agua','Refresco','Jugo','Energizante','Cerveza','Vino','Licor','Leche'], requerido: false, esVariante: false },
        { key: 'con_gas',     label: 'Con gas',      tipo: 'booleano', requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ALIM-FRU', nombre: 'Frutas y Verduras', emoji: '🥦', parentId: 'CAT-ALIM',
      atributos: [
        { key: 'unidad_venta', label: 'Unidad de venta', tipo: 'select', opciones: ['kg','pieza','manojo','bolsa','caja'], requerido: true, esVariante: false },
        { key: 'origen',       label: 'Origen',          tipo: 'select', opciones: ['Nacional','Importado','Orgánico'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ALIM-SNA', nombre: 'Snacks', emoji: '🍿', parentId: 'CAT-ALIM',
      atributos: [
        { key: 'sabor',  label: 'Sabor',          tipo: 'texto',   requerido: false, esVariante: true },
        { key: 'peso_g', label: 'Contenido (g)',  tipo: 'numero',  requerido: false, esVariante: false },
      ],
    },
    // ── Subcategorías de Electrónica ──────────────────────────────────────────
    {
      id: 'CAT-ELECT-CEL', nombre: 'Celulares', emoji: '📱', parentId: 'CAT-ELECT',
      atributos: [
        { key: 'modelo',  label: 'Modelo',    tipo: 'texto',  requerido: true,  esVariante: false },
        { key: 'ram',     label: 'RAM',        tipo: 'select', opciones: ['4GB','6GB','8GB','12GB','16GB'], requerido: false, esVariante: true },
        { key: 'bateria', label: 'Batería',    tipo: 'select', opciones: ['3000mAh','4000mAh','5000mAh','6000mAh+'], requerido: false, esVariante: false },
        { key: 'red',     label: 'Red',        tipo: 'select', opciones: ['4G','5G'], requerido: false, esVariante: false },
      ],
    },
    {
      id: 'CAT-ELECT-AUD', nombre: 'Audio', emoji: '🎧', parentId: 'CAT-ELECT',
      atributos: [
        { key: 'tipo_audio', label: 'Tipo',      tipo: 'select', opciones: ['In-ear','On-ear','Over-ear','Bocina','Barra de sonido'], requerido: false, esVariante: false },
        { key: 'conexion',   label: 'Conexión',  tipo: 'select', opciones: ['Bluetooth','Cable 3.5mm','USB-C','Inalámbrico 2.4GHz'], requerido: false, esVariante: false },
        { key: 'bateria_h',  label: 'Batería (hrs)', tipo: 'numero', requerido: false, esVariante: false },
      ],
    },
  ];

  administradores: Administrador[] = [
    {
      id: 'ADM-01', nombre: 'Carlos', apellido: 'Mendoza',
      telefono: '5551234567', pin: '1234', rol: 'superadmin',
      permisosExtra: {}, activo: true, fechaCreacion: '2024-01-15',
    },
    {
      id: 'ADM-02', nombre: 'Laura', apellido: 'García',
      telefono: '5559876543', pin: '5678', rol: 'gerente',
      permisosExtra: {}, activo: true, fechaCreacion: '2024-03-10',
    },
    {
      id: 'ADM-03', nombre: 'Miguel', apellido: 'Torres',
      telefono: '5554567890', pin: '9012', rol: 'vendedor',
      permisosExtra: { reportes: true }, activo: true, fechaCreacion: '2024-06-01',
    },
    {
      id: 'ADM-04', nombre: 'Ana', apellido: 'Ramírez',
      telefono: '5552345678', pin: '3456', rol: 'almacen',
      permisosExtra: {}, activo: false, fechaCreacion: '2024-08-20',
    },
  ];

  readonly ventasPorDia: VentaDia[] = [
    { fecha: '14 May', ventas: 3200,  pedidos: 5 },
    { fecha: '15 May', ventas: 1800,  pedidos: 3 },
    { fecha: '16 May', ventas: 4500,  pedidos: 7 },
    { fecha: '17 May', ventas: 2900,  pedidos: 4 },
    { fecha: '18 May', ventas: 6100,  pedidos: 9 },
    { fecha: '19 May', ventas: 3800,  pedidos: 6 },
    { fecha: '20 May', ventas: 1797,  pedidos: 2 },
  ];

  movimientos: MovimientoInventario[] = [
    { id: 'MOV-0001', loteId: 'LOT-001', tipo: 'entrada', productoId: 1, productoNombre: 'Audifonos Monster XKT20',  cantidad: 5,  sucursalDestinoId: 'SUC-01', nota: 'Compra inicial proveedor Monster', fecha: '2026-04-01T10:00:00' },
    { id: 'MOV-0002', loteId: 'LOT-001', tipo: 'entrada', productoId: 1, productoNombre: 'Audifonos Monster XKT20',  cantidad: 4,  sucursalDestinoId: 'SUC-02', nota: 'Compra inicial proveedor Monster', fecha: '2026-04-01T10:00:00' },
    { id: 'MOV-0003', loteId: 'LOT-001', tipo: 'entrada', productoId: 1, productoNombre: 'Audifonos Monster XKT20',  cantidad: 2,  sucursalDestinoId: 'MOV-01', nota: 'Compra inicial proveedor Monster', fecha: '2026-04-01T10:00:00' },
    { id: 'MOV-0004', loteId: 'LOT-001', tipo: 'entrada', productoId: 1, productoNombre: 'Audifonos Monster XKT20',  cantidad: 1,  sucursalDestinoId: 'MOV-02', nota: 'Compra inicial proveedor Monster', fecha: '2026-04-01T10:00:00' },
    { id: 'MOV-0005', loteId: 'LOT-002', tipo: 'entrada', productoId: 2, productoNombre: 'Diadema Moreka E72',        cantidad: 2,  sucursalDestinoId: 'SUC-01', nota: 'Compra inicial proveedor Moreka',  fecha: '2026-04-05T09:00:00' },
    { id: 'MOV-0006', loteId: 'LOT-002', tipo: 'entrada', productoId: 2, productoNombre: 'Diadema Moreka E72',        cantidad: 1,  sucursalDestinoId: 'SUC-02', nota: 'Compra inicial proveedor Moreka',  fecha: '2026-04-05T09:00:00' },
    { id: 'MOV-0007', loteId: 'LOT-003', tipo: 'entrada', productoId: 3, productoNombre: 'Cargador USB-C 20W',        cantidad: 10, sucursalDestinoId: 'SUC-01', nota: 'Reposición de cargadores',         fecha: '2026-04-10T11:00:00' },
    { id: 'MOV-0008', loteId: 'LOT-003', tipo: 'entrada', productoId: 3, productoNombre: 'Cargador USB-C 20W',        cantidad: 8,  sucursalDestinoId: 'SUC-02', nota: 'Reposición de cargadores',         fecha: '2026-04-10T11:00:00' },
    { id: 'MOV-0009', loteId: 'LOT-003', tipo: 'entrada', productoId: 3, productoNombre: 'Cargador USB-C 20W',        cantidad: 4,  sucursalDestinoId: 'MOV-01', nota: 'Reposición de cargadores',         fecha: '2026-04-10T11:00:00' },
    { id: 'MOV-0010', loteId: 'LOT-004', tipo: 'transferencia', productoId: 3, productoNombre: 'Cargador USB-C 20W',      cantidad: 5,  sucursalOrigenId: 'SUC-01', sucursalDestinoId: 'MOV-01', nota: 'TRF-001',                          fecha: '2026-05-14T09:00:00' },
    { id: 'MOV-0011', loteId: 'LOT-005', tipo: 'transferencia', productoId: 7, productoNombre: 'Cable Lightning 1M',      cantidad: 10, sucursalOrigenId: 'SUC-01', sucursalDestinoId: 'MOV-02', nota: 'TRF-002',                          fecha: '2026-05-13T11:00:00' },
    { id: 'MOV-0012', loteId: 'LOT-006', tipo: 'entrada',       productoId: 5, productoNombre: 'Bocina Bluetooth RGB',     cantidad: 5,  sucursalDestinoId: 'SUC-01',                              nota: 'Reposición bocinas',               fecha: '2026-05-12T08:30:00' },
    { id: 'MOV-0013', loteId: 'LOT-006', tipo: 'entrada',       productoId: 5, productoNombre: 'Bocina Bluetooth RGB',     cantidad: 3,  sucursalDestinoId: 'SUC-02',                              nota: 'Reposición bocinas',               fecha: '2026-05-12T08:30:00' },
    { id: 'MOV-0014', loteId: 'LOT-007', tipo: 'entrada',       productoId: 6, productoNombre: 'Power Bank 10000mAh',     cantidad: 8,  sucursalDestinoId: 'SUC-01',                              nota: 'Entrada proveedor GenPower',       fecha: '2026-05-11T10:00:00' },
    { id: 'MOV-0015', loteId: 'LOT-007', tipo: 'entrada',       productoId: 6, productoNombre: 'Power Bank 10000mAh',     cantidad: 6,  sucursalDestinoId: 'SUC-02',                              nota: 'Entrada proveedor GenPower',       fecha: '2026-05-11T10:00:00' },
    { id: 'MOV-0016', loteId: 'LOT-008', tipo: 'transferencia', productoId: 5, productoNombre: 'Bocina Bluetooth RGB',     cantidad: 2,  sucursalOrigenId: 'SUC-01', sucursalDestinoId: 'MOV-01', nota: 'TRF-003',                          fecha: '2026-05-10T14:00:00' },
    { id: 'MOV-0017', loteId: 'LOT-009', tipo: 'entrada',       productoId: 4, productoNombre: 'Smart Watch Moreka GT5',  cantidad: 3,  sucursalDestinoId: 'SUC-01',                              nota: 'Entrada proveedor Moreka',         fecha: '2026-05-09T09:15:00' },
    { id: 'MOV-0018', loteId: 'LOT-009', tipo: 'entrada',       productoId: 4, productoNombre: 'Smart Watch Moreka GT5',  cantidad: 2,  sucursalDestinoId: 'SUC-02',                              nota: 'Entrada proveedor Moreka',         fecha: '2026-05-09T09:15:00' },
    { id: 'MOV-0019', loteId: 'LOT-010', tipo: 'ajuste',        productoId: 7, productoNombre: 'Cable Lightning 1M',      cantidad: -3, sucursalOrigenId: 'SUC-02',                              nota: 'Merma detectada en conteo físico', fecha: '2026-05-08T16:00:00' },
    { id: 'MOV-0020', loteId: 'LOT-011', tipo: 'entrada',       productoId: 8, productoNombre: 'Control Gamer Retro',     cantidad: 4,  sucursalDestinoId: 'SUC-01',                              nota: 'Reposición controles',             fecha: '2026-05-07T11:30:00' },
    { id: 'MOV-0021', loteId: 'LOT-011', tipo: 'entrada',       productoId: 8, productoNombre: 'Control Gamer Retro',     cantidad: 3,  sucursalDestinoId: 'SUC-02',                              nota: 'Reposición controles',             fecha: '2026-05-07T11:30:00' },
  ];

  // ── Métodos de inventario ─────────────────────────────────────────────────

  stockTotalProducto(productoId: number): number {
    return this.inventario
      .filter(i => i.productoId === productoId)
      .reduce((s, i) => s + i.stock, 0);
  }

  stockDisponibleColor(productoId: number, sucursalId: string, color?: string): number {
    return this.inventario
      .filter(i => i.productoId === productoId && i.sucursalId === sucursalId && (color ? i.color === color : true))
      .reduce((s, i) => s + i.stock, 0);
  }

  registrarEntrada(
    productoId: number,
    distribuciones: { sucursalId: string; color?: string; cantidad: number }[],
    nota: string
  ): void {
    const producto = this.productos.find(p => p.id === productoId);
    if (!producto) return;

    const loteId = `LOT-${Date.now()}`;
    const fecha = new Date().toISOString();
    const activas = distribuciones.filter(d => d.cantidad > 0);

    activas.forEach((d, idx) => {
      this.movimientos.unshift({
        id: `MOV-${Date.now()}-${idx}`,
        loteId,
        tipo: 'entrada',
        productoId,
        productoNombre: producto.nombre,
        cantidad: d.cantidad,
        color: d.color,
        sucursalDestinoId: d.sucursalId,
        nota,
        fecha,
      });

      const item = this.inventario.find(i =>
        i.productoId === productoId &&
        i.sucursalId === d.sucursalId &&
        i.color === d.color
      );
      if (item) {
        (item as any).stock += d.cantidad;
      } else {
        this.inventario.push({
          productoId, productoNombre: producto.nombre,
          sucursalId: d.sucursalId, color: d.color, stock: d.cantidad,
        });
      }
    });

    (producto as any).stock = this.stockTotalProducto(productoId);
  }

  registrarTransferencia(
    productoId: number,
    origenId: string,
    destinoId: string,
    cantidad: number,
    nota: string,
    color?: string
  ): void {
    const producto = this.productos.find(p => p.id === productoId);
    if (!producto) return;

    const loteId = `LOT-${Date.now()}`;
    const fecha = new Date().toISOString();

    this.movimientos.unshift({
      id: `MOV-${Date.now()}`,
      loteId,
      tipo: 'transferencia',
      productoId,
      productoNombre: producto.nombre,
      cantidad,
      color,
      sucursalOrigenId: origenId,
      sucursalDestinoId: destinoId,
      nota,
      fecha,
    });

    const origen = this.inventario.find(i =>
      i.productoId === productoId && i.sucursalId === origenId && i.color === color
    );
    if (origen) (origen as any).stock = Math.max(0, origen.stock - cantidad);

    const destino = this.inventario.find(i =>
      i.productoId === productoId && i.sucursalId === destinoId && i.color === color
    );
    if (destino) {
      (destino as any).stock += cantidad;
    } else {
      this.inventario.push({ productoId, productoNombre: producto.nombre, sucursalId: destinoId, color, stock: cantidad });
    }

    (producto as any).stock = this.stockTotalProducto(productoId);

    this.transferencias.unshift({
      id: `TRF-${String(this.transferencias.length + 1).padStart(3, '0')}`,
      origen: origenId,
      destino: destinoId,
      productoNombre: producto.nombre,
      cantidad,
      fecha,
      status: 'completado',
    });
  }

  // ── Métodos de categorías ─────────────────────────────────────────────────

  getCategoryPath(id: string): CategoriaProducto[] {
    const path: CategoriaProducto[] = [];
    let current: CategoriaProducto | undefined = this.categorias.find(c => c.id === id);
    while (current) {
      path.unshift(current);
      const parentId = current.parentId ?? null;
      current = parentId ? this.categorias.find(c => c.id === parentId) : undefined;
    }
    return path;
  }

  getAllAtributosHerencia(categoriaId: string): AtributoDefinicion[] {
    return this.getCategoryPath(categoriaId).flatMap(c => c.atributos);
  }

  getCategoryLabel(categoriaId: string): string {
    return this.getCategoryPath(categoriaId).map(c => c.nombre).join(' › ');
  }

  getSucursalNombre(id: string): string {
    return this.sucursales.find(s => s.id === id)?.nombre ?? id;
  }

  stockTotalSucursal(sucursalId: string): number {
    return this.inventario.filter(i => i.sucursalId === sucursalId).reduce((acc, i) => acc + i.stock, 0);
  }

  addSucursal(nombre: string, tipo: 'fijo' | 'movil', direccion: string): void {
    const prefix = tipo === 'fijo' ? 'SUC' : 'MOV';
    const existing = this.sucursales.filter(s => s.id.startsWith(prefix + '-'));
    const nums = existing.map(s => parseInt(s.id.split('-')[1] ?? '0', 10));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    this.sucursales.push({ id: `${prefix}-${String(next).padStart(2, '0')}`, nombre, tipo, direccion });
  }

  updateSucursal(id: string, nombre: string, tipo: 'fijo' | 'movil', direccion: string): void {
    const s = this.sucursales.find(s => s.id === id);
    if (s) { s.nombre = nombre; s.tipo = tipo; s.direccion = direccion; }
  }

  deleteSucursal(id: string): void {
    const idx = this.sucursales.findIndex(s => s.id === id);
    if (idx !== -1) this.sucursales.splice(idx, 1);
  }
}
